import React from 'react';
import { Result, Button, Spin } from 'antd';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from 'contexts/PermissionContext';

/**
 * Permission Guard Component
 * 
 * Wraps a component and checks if user has permission to access it.
 * Can check both resource-based permissions and route-based access.
 * 
 * Usage:
 * <PermissionGuard resource="users" action="read">
 *   <UserList />
 * </PermissionGuard>
 * 
 * Or for route-based:
 * <PermissionGuard routeKey="express-nextgen-users">
 *   <UserList />
 * </PermissionGuard>
 */
const PermissionGuard = ({ 
  children, 
  resource, 
  action = 'read', 
  routeKey,
  fallback = null,
  showForbidden = true 
}) => {
  const navigate = useNavigate();
  const { hasPermission, canAccessRoute, loading, user } = usePermissions();

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: 200 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  // If no user after loading, deny access
  if (!user) {
    return (
      <Result
        status="403"
        title="Access Denied"
        subTitle="You must be logged in to access this page."
        extra={
          <Button type="primary" onClick={() => navigate('/auth/login')}>
            Login
          </Button>
        }
      />
    );
  }

  // Check permission
  let hasAccess = false;
  
  if (routeKey) {
    hasAccess = canAccessRoute(routeKey);
  } else if (resource) {
    hasAccess = hasPermission(resource, action);
  } else {
    // No permission requirement specified, allow access
    hasAccess = true;
  }

  if (hasAccess) {
    return children;
  }

  // No access - show fallback or forbidden message
  if (fallback) {
    return fallback;
  }

  if (!showForbidden) {
    return null;
  }

  return (
    <Result
      status="403"
      title="Access Denied"
      subTitle={`You don't have permission to ${action} ${resource || 'this page'}.`}
      extra={
        <Button type="primary" onClick={() => navigate(-1)}>
          Go Back
        </Button>
      }
    />
  );
};

/**
 * Higher-order component version
 */
export const withPermission = (WrappedComponent, { resource, action, routeKey }) => {
  return function WithPermissionComponent(props) {
    return (
      <PermissionGuard resource={resource} action={action} routeKey={routeKey}>
        <WrappedComponent {...props} />
      </PermissionGuard>
    );
  };
};

/**
 * Hook to check permissions in components
 */
export const useHasPermission = (resource, action = 'read') => {
  const { hasPermission, loading } = usePermissions();
  return { hasPermission: hasPermission(resource, action), loading };
};

export default PermissionGuard;
